import {
    Plan,
    StripeConfig,
    StripePlan,
    Coupon,
    StripeCoupon,
    Event,
    League,
    Channel,
    User,
    UserRequest,
    StripeUserRequest,
    StripeUser,
    UserPlan,
    SessionRequest,
    Session,
    RefundRequest,
    UserPlanRequest,
    CDNRegion,
    CDNRegionDeactivate,
    VideoQuality,
    Application,
    System,
    Region,
    EmailRequest,
    PaypalConfig,
    PlisioConfig,
    SquareConfig,
    CpayConfig,
    AdminUserDevice,
    CardPaymentConfiguration,
    CardPaymentConfigurations,
    CalLeague,
    CalMatch,
    CalMatchDraft,
    CalMatchPageRequest,
    CalMatchPageResponse,
    CalChannel,
    MatchEvent,
    CalEventChannel,
    ApplyDefaultSourcesResult,
    OccupiedEvent,
    ChannelConflict,
    IcsPreviewResult,
    CalendarStaticData
} from "@/model/models";
import apiClient from '@/utils/api-client'
import FunctionService from '@/services/function-service'
import { cachedInSession, invalidateSessionCache, SESSION_CACHE_KEYS } from '@/utils/session-cache'

// Query params for the All Matches date window - only sent when both ends are set, so the
// BE keeps returning everything when there's no window.
function windowParams(from?: string, to?: string) {
    return from && to ? { from, to } : {}
}

class AdminService {
    public getStripeConfigurationList() {
        return new Promise<StripeConfig[]>((resolve, reject) => {
            apiClient.get('/api/v1/admin/stripe-config/list')
                .then((res) => {
                    resolve(res.data.stripeConfigurationList);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public updateStripeConfiguration(stripeConfig: StripeConfig) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.put('/api/v1/admin/stripe-config/update', stripeConfig).then((res) => {
                resolve(res.data.toString())
            }).catch((error) => {
                reject(error)
            })
        })
    }
    public insertStripeConfiguration(stripeConfig: StripeConfig) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.post('/api/v1/admin/stripe-config/insert', stripeConfig).then((res) => {
                if (res.data.code == 6003){
                    reject(new Error(res.data.message))
                } else {
                    resolve(res.data.message)
                }
            }).catch((error) => {
                reject(error)
            })
        })
    }

    public getPaypalConfigurationList() {
        return new Promise<PaypalConfig[]>((resolve, reject) => {
            apiClient.get('/api/v1/admin/paypal-config/list')
                .then((res) => {
                    resolve(res.data.paypalConfigurationList);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public updatePaypalConfiguration(paypalConfig: PaypalConfig) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.put('/api/v1/admin/paypal-config/update', paypalConfig).then((res) => {
                resolve(res.data.toString())
            }).catch((error) => {
                reject(error)
            })
        })
    }
    public insertPaypalConfiguration(paypalConfig: PaypalConfig) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.post('/api/v1/admin/paypal-config/insert', paypalConfig).then((res) => {
                if (res.data.code == 6013){
                    reject(new Error(res.data.message))
                } else {
                    resolve(res.data.message)
                }
            }).catch((error) => {
                reject(error)
            })
        })
    }

    public getPlisioConfigurationList() {
        return new Promise<PlisioConfig[]>((resolve, reject) => {
            apiClient.get('/api/v1/admin/plisio-config/list')
                .then((res) => {
                    resolve(res.data.plisioConfigurationList);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public updatePlisioConfiguration(plisioConfig: PlisioConfig) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.put('/api/v1/admin/plisio-config/update', plisioConfig)
                .then((res) => {
                    resolve(res.data.toString());
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public insertPlisioConfiguration(plisioConfig: PlisioConfig) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.post('/api/v1/admin/plisio-config/insert', plisioConfig)
                .then((res) => {
                    if (res.data.code == 6023) {
                        reject(new Error(res.data.message));
                    } else {
                        resolve(res.data.message);
                    }
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public getCardPaymentConfigurationList() {
        return new Promise<CardPaymentConfiguration[]>((resolve, reject) => {
            apiClient.get('/api/v1/payment-configuration/card/list')
                .then((res) => {
                    resolve(res.data.cardPaymentConfigurationList);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public saveCardPaymentConfigurations(cardPaymentConfigurations: CardPaymentConfigurations) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.put('/api/v1/payment-configuration/card/save', cardPaymentConfigurations)
                .then((res) => {
                    resolve(res.data.toString());
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public getSquareConfigurationList() {
        return new Promise<SquareConfig[]>((resolve, reject) => {
            apiClient.get('/api/v1/admin/square-config/list')
                .then((res) => {
                    resolve(res.data.squareConfigurationList);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public updateSquareConfiguration(squareConfig: SquareConfig) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.put('/api/v1/admin/square-config/update', squareConfig)
                .then((res) => {
                    resolve(res.data.toString());
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public insertSquareConfiguration(squareConfig: SquareConfig) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.post('/api/v1/admin/square-config/insert', squareConfig)
                .then((res) => {
                    if (res.data.code == 6033) {
                        reject(new Error(res.data.message));
                    } else {
                        resolve(res.data.message);
                    }
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    // cPay only exposes list + update/list today (no insert, no delete endpoint)
    public getCpayConfigurationList() {
        return new Promise<CpayConfig[]>((resolve, reject) => {
            apiClient.get('/api/v1/cpay/configuration/list')
                .then((res) => {
                    resolve(res.data.cpayConfigurationList);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public updateCpayConfiguration(cpayConfig: CpayConfig) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.put('/api/v1/cpay/configuration/update/list', { cpayConfigurationList: [cpayConfig] })
                .then((res) => {
                    resolve(res.data.toString());
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public getPlansList() {
        return new Promise<Plan[]>((resolve, reject) => {
            apiClient.get('/api/v1/admin/plan/list')
                .then((res) => {
                    resolve(res.data.planList);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }
    public updatePlan(plan: Plan) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.put('/api/v1/admin/plan/update', plan).then((res) => {
                resolve(res.data.toString())
            }).catch((error) => {
                console.log("error"+error)
                reject(error)
            })
        })
    }
    public insertPlan(plan: Plan) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.post('/api/v1/admin/plan/insert', plan).then((res) => {
                if (res.data.code == 3002){
                    reject(new Error(res.data.message))
                } else {
                    resolve(res.data.message)
                }
            }).catch((error) => {
                reject(error)
            })
        })
    }
    public getStripePlansList() {
        return new Promise<StripePlan[]>((resolve, reject) => {
            apiClient.get('/api/v1/admin/stripe-plan/list')
                .then((res) => {
                    resolve(res.data.stripePlanList);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }
    public updateStripePlan(stripePlan: StripePlan) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.put('/api/v1/admin/stripe-plan/update', stripePlan).then((res) => {
                resolve(res.data.toString())
            }).catch((error) => {
                console.log("error"+error)
                reject(error)
            })
        })
    }
    public insertStripePlan(stripePlan: StripePlan) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.post('/api/v1/admin/stripe-plan/insert', stripePlan).then((res) => {
                if (res.data.code == 6004){
                    reject(new Error(res.data.message))
                } else {
                    resolve(res.data.message)
                }
            }).catch((error) => {
                reject(error)
            })
        })
    }
    public getCouponsList() {
        return new Promise<Coupon[]>((resolve, reject) => {
            apiClient.get('/api/v1/admin/coupon/list')
                .then((res) => {
                    resolve(res.data.couponList);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }
    public updateCoupon(coupon: Coupon) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.put('/api/v1/admin/coupon/update', coupon).then((res) => {
                resolve(res.data.toString())
            }).catch((error) => {
                console.log("error"+error)
                reject(error)
            })
        })
    }
    public insertCoupon(coupon: Coupon) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.post('/api/v1/admin/coupon/insert', coupon).then((res) => {
                if (res.data.code == 500){
                    reject(new Error(res.data.message))
                } else {
                    resolve(res.data.message)
                }
            }).catch((error) => {
                reject(error)
            })
        })
    }
    public getStripeCouponsList() {
        return new Promise<StripeCoupon[]>((resolve, reject) => {
            apiClient.get('/api/v1/admin/stripe-coupon/list')
                .then((res) => {
                    resolve(res.data.stripeCouponList);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }
    public updateStripeCoupon(stripeCoupon: StripeCoupon) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.put('/api/v1/admin/stripe-coupon/update', stripeCoupon).then((res) => {
                if (res.data.code == 6005){
                    reject(new Error(res.data.message))
                } else {
                    resolve(res.data.message)
                }
            }).catch((error) => {
                reject(error)
            })
        })
    }
    public insertStripeCoupon(stripeCoupon: StripeCoupon) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.post('/api/v1/admin/stripe-coupon/insert', stripeCoupon).then((res) => {
                if (res.data.code == 6005){
                    reject(new Error(res.data.message))
                } else {
                    resolve(res.data.message)
                }
            }).catch((error) => {
                reject(error)
            })
        })
    }


    // cache=true serves/stores the sessionStorage copy (calendar screens); the default stays
    // live. The copy is dropped by insertEvent/updateEvent below.
    public getEventsList(cache = false): Promise<Event[]> {
        return cache
            ? cachedInSession(SESSION_CACHE_KEYS.events, () => this.fetchEventsList())
            : this.fetchEventsList();
    }

    private fetchEventsList() {
        return new Promise<Event[]>((resolve, reject) => {
            apiClient.get('/api/v1/admin/event/list')
                .then((res) => {
                    resolve(res.data.eventList);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }
    public updateEvent(event: Event) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.put('/api/v1/admin/event/update', event).then((res) => {
                invalidateSessionCache(SESSION_CACHE_KEYS.events)
                if (res.data.code == 6005){
                    console.log("6005")
                    reject(new Error(res.data.message))
                } else {
                    resolve(res.data.message)
                }
            }).catch((error) => {
                reject(error)
            })
        })
    }
    public insertEvent(event: Event) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.post('/api/v1/admin/event/insert', event).then((res) => {
                invalidateSessionCache(SESSION_CACHE_KEYS.events)
                if (res.data.code == 6005){
                    reject(new Error(res.data.message))
                } else {
                    resolve(res.data.message)
                }
            }).catch((error) => {
                reject(error)
            })
        })
    }
    public getLeaguesList() {
        return new Promise<League[]>((resolve, reject) => {
            apiClient.get('/api/v1/admin/league/list')
                .then((res) => {
                    resolve(res.data.leagueList);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }
    public updateLeague(league: League) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.put('/api/v1/admin/league/update', league).then((res) => {
                if (res.data.code == 4007 || res.data.code == 4006) {
                    reject(new Error(res.data.message))
                } else {
                    resolve(res.data.message)
                }
            }).catch((error) => {
                reject(error)
            })
        })
    }
    public insertLeague(league: League) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.post('/api/v1/admin/league/insert', league).then((res) => {
                if (res.data.code == 4007) {
                    reject(new Error(res.data.message))
                } else {
                    resolve(res.data.message)
                }
            }).catch((error) => {
                reject(error)
            })
        })
    }
    // application omitted (or '') returns every channel, unfiltered - what the cal_channel
    // source picker wants, since a calendar is application-agnostic.
    // cache=true serves/stores ONE sessionStorage copy of the unfiltered list (calendar
    // screens); the default stays live because the broadcast/management pages rely on fresh
    // liveViews/status/providersMetadata. Dropped by insertChannel/updateChannel below.
    public getChannelsList(application?: string, cache = false): Promise<Channel[]> {
        return cache
            ? cachedInSession(SESSION_CACHE_KEYS.channels, () => this.fetchChannelsList())
            : this.fetchChannelsList(application);
    }

    private fetchChannelsList(application?: string) {
        return new Promise<Channel[]>((resolve, reject) => {
            apiClient.get('/api/v1/admin/channel/list', {
                params: { application: application || '' } })
                .then((res) => {
                    resolve(res.data.channelList);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }
    public updateChannel(channel: Channel) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.put('/api/v1/admin/channel/update', channel).then((res) => {
                invalidateSessionCache(SESSION_CACHE_KEYS.channels)
                resolve(res.data.toString())
            }).catch((error) => {
                console.log("error"+error)
                reject(error)
            })
        })
    }
    public insertChannel(channel: Channel) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.post('/api/v1/admin/channel/insert', channel).then((res) => {
                invalidateSessionCache(SESSION_CACHE_KEYS.channels)
                if (res.data.code == 7001){
                    reject(new Error(res.data.message))
                } else {
                    resolve(res.data.message)
                }
            }).catch((error) => {
                reject(error)
            })
        })
    }
    // --- Calendar import (cal_league) ---

    // cache=true serves/stores the sessionStorage copy (calendar screens); the default stays
    // live. The copy is dropped by updateCalLeague/confirmCalLeagueImport below.
    public getCalLeaguesList(cache = false): Promise<CalLeague[]> {
        return cache
            ? cachedInSession(SESSION_CACHE_KEYS.calLeagues, () => this.fetchCalLeaguesList())
            : this.fetchCalLeaguesList();
    }

    private fetchCalLeaguesList() {
        return new Promise<CalLeague[]>((resolve, reject) => {
            apiClient.get('/api/v1/admin/cal-league/list')
                .then((res) => {
                    resolve(res.data.calLeagueList);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    // The confirmed-match view for one calendar - read-only, populated once matches have
    // actually been written via confirmCalLeagueImport.
    public getCalMatchesList(calLeagueId: number) {
        return new Promise<CalMatch[]>((resolve, reject) => {
            apiClient.get('/api/v1/admin/cal-league/match/list', { params: { calLeagueId } })
                .then((res) => {
                    resolve(res.data.calMatchList);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    // Adds one match by hand to a calendar (a fixture that isn't in its .ics). The BE generates
    // the uid ("manual-..."), so a later .ics re-upload never overwrites it. Resolves with the
    // created match.
    public createCalMatch(match: { calLeagueId: number, team1: string, team2: string, venue?: string,
                                   startTime: string, endTime?: string, format?: string, calStatus?: string }) {
        return new Promise<CalMatch>((resolve, reject) => {
            apiClient.post('/api/v1/admin/cal-league/match/create', match)
                .then((res) => resolve(res.data))
                .catch(reject);
        });
    }

    // One match by id - loads the match edit page.
    public getCalMatch(calMatchId: number) {
        return new Promise<CalMatch>((resolve, reject) => {
            apiClient.get('/api/v1/admin/cal-league/match/get', { params: { calMatchId } })
                .then((res) => resolve(res.data))
                .catch(reject);
        });
    }

    // Manual correction of one match: teams, start/end time, venue (arena), format and calStatus. Resolves with
    // the updated match. endTime/venue/format null = cleared.
    public updateCalMatch(match: { calMatchId: number, team1: string, team2: string, startTime: string,
                                   endTime?: string | null, venue?: string | null, format?: string | null,
                                   calStatus?: string }) {
        return new Promise<CalMatch>((resolve, reject) => {
            apiClient.put('/api/v1/admin/cal-league/match/update', match)
                .then((res) => {
                    if (res.data && res.data.code) {
                        reject(new Error(res.data.message));
                    } else {
                        resolve(res.data);
                    }
                })
                .catch(reject);
        });
    }

    // Every cal_channel row across every match in one calendar, in one call.
    public getCalChannelsList(calLeagueId: number) {
        return new Promise<CalChannel[]>((resolve, reject) => {
            apiClient.get('/api/v1/admin/cal-league/channel/list', { params: { calLeagueId } })
                .then((res) => {
                    resolve(res.data.calChannelList);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    // Upserts by calChannelId (update) or creates new when omitted - one call covers a
    // per-match override, a skip (call with active:false), a reset-to-default, and one
    // iteration of "apply to all". See CalMatches.vue's saveSlot/upsertChannel for how the
    // caller decides which of those this is.
    public upsertCalChannel(calChannel: CalChannel) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.put('/api/v1/admin/cal-league/channel/update', calChannel).then((res) => {
                if (res.data.code) {
                    reject(new Error(res.data.message));
                } else {
                    resolve(true);
                }
            }).catch((error) => {
                reject(error);
            });
        });
    }

    // Stamps the league's default sources onto its active matches in one backend call. Matches that clash with
    // another match of the league, or with an existing source, get nothing and come back in notSet.
    public applyDefaultSources(calLeagueId: number, sources: Record<string, unknown>[]) {
        return new Promise<ApplyDefaultSourcesResult>((resolve, reject) => {
            apiClient.put('/api/v1/admin/cal-league/channel/apply-default', { calLeagueId, sources }).then((res) => {
                if (res.data && res.data.code) {
                    reject(new Error(res.data.message));
                } else {
                    resolve(res.data);
                }
            }).catch(reject);
        });
    }

    // Records a manual stop (Live Events) after the stream was stopped through the b-api: status=false.
    // active and the event links are left as they are. It does not stop the encoder: call the b-api stop first.
    public stopCalChannels(calChannelIds: number[]) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.put('/api/v1/admin/cal-league/channel/stop', { calChannelIds }).then((res) => {
                if (res.data.code) {
                    reject(new Error(res.data.message));
                } else {
                    resolve(true);
                }
            }).catch((error) => {
                reject(error);
            });
        });
    }

    // Which of this league's cal_channel rows overlap some other active cal_channel row
    // anywhere (channels aren't league-scoped, unlike events) - backs the Sources panel's
    // conflict indicator, both the aggregate "X/4" cell and CalChannelSlotEditor itself.
    public getChannelConflicts(calLeagueId: number) {
        return new Promise<ChannelConflict[]>((resolve, reject) => {
            apiClient.get('/api/v1/admin/cal-league/channel/conflicts', { params: { calLeagueId } })
                .then((res) => {
                    resolve(res.data.channelConflictList);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public getMatchEventsList(calLeagueId: number) {
        return new Promise<MatchEvent[]>((resolve, reject) => {
            apiClient.get('/api/v1/admin/cal-league/match-event/list', { params: { calLeagueId } })
                .then((res) => {
                    resolve(res.data.matchEventList);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    // Upserts by matchEventId (update) or by the natural key (calMatchId, eventId) when
    // omitted - UNIQUE(cal_match_id, event_id) on the BE means re-picking the same event for
    // this match updates in place. See CalMatches.vue's saveMatchEvent for how it's used.
    public upsertMatchEvent(matchEvent: MatchEvent) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.put('/api/v1/admin/cal-league/match-event/update', matchEvent).then((res) => {
                if (res.data.code) {
                    reject(new Error(res.data.message));
                } else {
                    resolve(true);
                }
            }).catch((error) => {
                reject(error);
            });
        });
    }

    // The join table backing the Match Events page's per-event channel checkbox list (see
    // docs/be-tasks/cal-event-channel-link.md, Option 2) - which of this match's own
    // CalChannel feeds carry a given match_events row.
    public getCalEventChannelsList(calLeagueId: number) {
        return new Promise<CalEventChannel[]>((resolve, reject) => {
            apiClient.get('/api/v1/admin/cal-league/cal-event-channel/list', { params: { calLeagueId } })
                .then((res) => {
                    resolve(res.data.calEventChannelList);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    // Upserts by calEventChannelId (update) or by the natural key (matchEventId,
    // calChannelId) when omitted - same convention as upsertMatchEvent/upsertCalChannel.
    // Checking/unchecking a channel box calls this with active:true/false (soft link/unlink,
    // never a hard delete), see MatchEvents.vue's onToggleChannel.
    public upsertCalEventChannel(calEventChannel: CalEventChannel) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.put('/api/v1/admin/cal-league/cal-event-channel/update', calEventChannel).then((res) => {
                if (res.data.code) {
                    reject(new Error(res.data.message));
                } else {
                    resolve(true);
                }
            }).catch((error) => {
                reject(error);
            });
        });
    }

    // Replaces the whole set of channels linked to one match_events row in ONE request/transaction:
    // the listed calChannelIds end up linked, every other channel of that row ends up unlinked.
    // Used by MatchEvents.vue's Save on the Channels editor (no request per checkbox click).
    public setCalEventChannels(matchEventId: number, calChannelIds: number[]) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.put('/api/v1/admin/cal-league/cal-event-channel/set', { matchEventId, calChannelIds }).then((res) => {
                if (res.data.code) {
                    reject(new Error(res.data.message));
                } else {
                    resolve(true);
                }
            }).catch((error) => {
                reject(error);
            });
        });
    }

    // Every OTHER league's active match_events assignments, as busy windows only - the events
    // picker's cross-league conflict check. This league's own matches/match_events are already
    // covered by getCalMatchesList/getMatchEventsList above; CalMatches.vue merges both into
    // one taken-events map.
    public getOccupiedEventsOutsideLeague(calLeagueId: number) {
        return new Promise<OccupiedEvent[]>((resolve, reject) => {
            apiClient.get('/api/v1/admin/cal-league/match-event/occupied-elsewhere', { params: { calLeagueId } })
                .then((res) => {
                    resolve(res.data.occupiedEventList);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    // --- Cross-league (every calendar) - same data as the per-league calls above, just
    // unfiltered by calLeagueId. Used by Daily.vue; the All Matches lists use getCalMatchesPaged. ---

    // from/to (ISO instants, both or neither) narrow the list to matches overlapping that
    // window. Omitted = every match.
    public getAllCalMatches(from?: string, to?: string) {
        return new Promise<CalMatch[]>((resolve, reject) => {
            apiClient.get('/api/v1/admin/cal-league/match/list-all', { params: windowParams(from, to) })
                .then((res) => {
                    resolve(res.data.calMatchList);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    // One BE-sorted page of matches across every league - All Matches / All Match Events.
    // Same request/response shape as getSessions.
    public getCalMatchesPaged(request: CalMatchPageRequest) {
        return new Promise<CalMatchPageResponse>((resolve, reject) => {
            apiClient.post('/api/v1/admin/cal-league/match/list-paged', request)
                .then((res) => {
                    resolve(res.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public getAllCalChannels(from?: string, to?: string) {
        return new Promise<CalChannel[]>((resolve, reject) => {
            apiClient.get('/api/v1/admin/cal-league/channel/list-all', { params: windowParams(from, to) })
                .then((res) => {
                    resolve(res.data.calChannelList);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    // from/to (ISO instants, both or neither) narrow the list to matches overlapping that
    // window - same convention as getAllCalMatches/getAllCalChannels. Omitted = every row,
    // as before (still needed unscoped by the All Match Events page).
    public getAllMatchEvents(from?: string, to?: string) {
        return new Promise<MatchEvent[]>((resolve, reject) => {
            apiClient.get('/api/v1/admin/cal-league/match-event/list-all', { params: windowParams(from, to) })
                .then((res) => {
                    resolve(res.data.matchEventList);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    // from/to narrow the list the same way getAllMatchEvents does - a link's window is its
    // match_events row's match, so this filters by the same match window, not its own dates.
    public getAllCalEventChannels(from?: string, to?: string) {
        return new Promise<CalEventChannel[]>((resolve, reject) => {
            apiClient.get('/api/v1/admin/cal-league/cal-event-channel/list-all', { params: windowParams(from, to) })
                .then((res) => {
                    resolve(res.data.calEventChannelList);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public getAllChannelConflicts(from?: string, to?: string) {
        return new Promise<ChannelConflict[]>((resolve, reject) => {
            apiClient.get('/api/v1/admin/cal-league/channel/conflicts/all', { params: windowParams(from, to) })
                .then((res) => {
                    resolve(res.data.channelConflictList);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }


    public updateCalLeague(calLeague: CalLeague) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.put('/api/v1/admin/cal-league/update', calLeague).then((res) => {
                invalidateSessionCache(SESSION_CACHE_KEYS.calLeagues)
                resolve(res.data.toString())
            }).catch((error) => {
                reject(error)
            })
        })
    }

    // Step 1 — parse only, nothing persisted. When calLeagueId is given (a
    // re-upload), the BE should still only *read* the existing league's matches
    // to annotate the preview (e.g. which uids are new vs. already known) —
    // it must not write anything until confirmCalLeagueImport is called.
    public previewCalLeague(file: File, calLeagueId?: number) {
        return new Promise<IcsPreviewResult>((resolve, reject) => {
            const formData = new FormData();
            formData.append('file', file);
            if (calLeagueId) {
                formData.append('calLeagueId', String(calLeagueId));
            }

            apiClient.post('/api/v1/admin/cal-league/preview', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            }).then((res) => {
                if (res.data.code) {
                    reject(new Error(res.data.message))
                    return
                }
                const matches = (res.data.matches || []).map((m: CalMatchDraft) => new CalMatchDraft(
                    m.uid, m.parentUid, m.team1, m.team2, m.venue, m.startTime, m.endTime,
                    m.format, m.calStatus
                ));
                resolve(new IcsPreviewResult(res.data.matchesParsed, res.data.teamsMatched, res.data.errors, matches, res.data.calendarUid, res.data.calendarName, res.data.scheduleUrl))
            }).catch((error) => {
                reject(error)
            })
        })
    }

    // Step 2 — persists what the admin reviewed/edited on the preview screen.
    // No calLeagueId: creates a new cal_league + its cal_match rows (matches may be empty -
    // saves the league alone, without a file). With calLeagueId: re-upload path — upserts each
    // match by uid, same as before, just deferred until now instead of happening at upload time.
    public confirmCalLeagueImport(name: string, shortName: string, matches: CalMatchDraft[], calLeagueId?: number, calendarUid?: string, sport?: string, calendarSourceUrl?: string) {
        return new Promise<CalLeague>((resolve, reject) => {
            const body: Record<string, unknown> = { name, shortName, matches };
            if (sport) {
                body.sport = sport;
            }
            if (calendarSourceUrl) {
                body.calendarSourceUrl = calendarSourceUrl;
            }
            if (calLeagueId) {
                body.calLeagueId = calLeagueId;
            }
            // Used on a first import, and on a re-upload only when the league has no uid yet
            // (saved without a file) - the BE ignores it for a league that already has one.
            if (calendarUid) {
                body.calendarUid = calendarUid;
            }
            apiClient.post('/api/v1/admin/cal-league/confirm', body).then((res) => {
                invalidateSessionCache(SESSION_CACHE_KEYS.calLeagues)
                if (res.data.code) {
                    reject(new Error(res.data.message))
                } else {
                    resolve(res.data.calLeague)
                }
            }).catch((error) => {
                reject(error)
            })
        })
    }

    public getUsersList(userRequest: UserRequest) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.post('/api/v1/admin/user/list', userRequest).then((res) => {
                if (res.status === 200 && res.data !== null) {
                    resolve(res.data);
                } else {
                    resolve(false)
                }
            }).catch((error) => {
                reject(error)
            })
        })
    }
    public updateUser(user: User) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.put('/api/v1/admin/user/update', user).then((res) => {
                resolve(res.data.toString())
            }).catch((error) => {
                console.log("error"+error)
                reject(error)
            })
        })
    }
    public getStripeUserList(stripeUserRequest: StripeUserRequest) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.post('/api/v1/admin/stripe-user/list', stripeUserRequest).then((res) => {
                if (res.status === 200 && res.data !== null) {
                    resolve(res.data);
                } else {
                    resolve(false)
                }
            }).catch((error) => {
                reject(error)
            })
        })
    }

    public insertStripeUser(stripeUser: StripeUser) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.post('/api/v1/admin/stripe-user/insert', stripeUser).then((res) => {
                if (res.data.code == 6006){
                    reject(new Error(res.data.message))
                } else {
                    resolve(res.data.message)
                }
            }).catch((error) => {
                console.log("error.data.message"+error.data.message)
                reject(error)
            })
        })
    }

    public updateStripeUser(stripeUser: StripeUser) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.put('/api/v1/admin/stripe-user/update', stripeUser).then((res) => {
                resolve(res.data.toString())
            }).catch((error) => {
                console.log("error"+error)
                reject(error)
            })
        })
    }

    public getUserPlanList(userPlanRequest: UserPlanRequest) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.post('/api/v1/admin/user-plan/list', userPlanRequest).then((res) => {
                if (res.status === 200 && res.data !== null) {
                    resolve(res.data);
                } else {
                    resolve(false)
                }
            }).catch((error) => {
                reject(error)
            })
        })
    }

    public insertUserPlan(userPlan: UserPlan) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.post('/api/v1/admin/user-plan/insert', userPlan).then((res) => {
                if (res.data.code == 6006){
                    reject(new Error(res.data.message))
                } else {
                    resolve(res.data.message)
                }
            }).catch((error) => {
                console.log("error.data.message"+error.data.message)
                reject(error)
            })
        })
    }

    public updateUserPlan(userPlan: UserPlan) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.put('/api/v1/admin/user-plan/update', userPlan).then((res) => {
                resolve(res.data.toString())
            }).catch((error) => {
                console.log("error"+error)
                reject(error)
            })
        })
    }
    public getSessions(sessionRequest: SessionRequest) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.post('/api/v1/admin/session/list', sessionRequest).then((res) => {
                if (res.status === 200 && res.data !== null) {
                    resolve(res.data);
                } else {
                    resolve(false)
                }
            }).catch((error) => {
                reject(error)
            })
        })
    }
    public updateSession(session: Session) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.put('/api/v1/admin/session/update', session).then((res) => {
                resolve(res.data.toString())
            }).catch((error) => {
                console.log("error"+error)
                reject(error)
            })
        })
    }

    public getNumberOfActiveSessions() {
        return new Promise<string>((resolve, reject) => {
            apiClient.get('/api/v1/admin/session/active').then((res) => {
                resolve(res.data);
            })
            .catch((error) => {
                reject(error);
            });
        });
    }

    public clearActiveSessions() {
        return new Promise<string>((resolve, reject) => {
            apiClient.get('/api/v1/admin/session/clear')
                .then((res) => {
                    resolve(res.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public refundUser(refundRequest: RefundRequest) {
        return new Promise<string>((resolve, reject) => {
            apiClient.post('/api/v1/admin/refund', refundRequest)
                .then((res) => {
                    if (res.data.code == 0) {
                        resolve(res.data.message)
                    } else {
                        reject(new Error(res.data.message))
                    }
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public getDevices(email: string) {
        return new Promise<AdminUserDevice[]>((resolve, reject) => {
            apiClient.get('/api/v1/admin/device/email/' + email)
                .then((res) => {
                    resolve(res.data)
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public getEmailList() {
        return new Promise<string>((resolve, reject) => {
            apiClient.get('/api/v1/admin/user/emails').then((res) => {
                resolve(res.data);
            })
            .catch((error) => {
                reject(error);
            });
        });
    }

    public suggestUsers(q: string): Promise<string[]> {
        return new Promise<string[]>((resolve, reject) => {
            apiClient.get('/api/v1/admin/user/suggest', { params: { q } })
                .then((res) => {
                    resolve(res.data.map((item: any) => item.email));
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public getApplications() {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.get('/api/v1/admin/applications/list').then((res) => {
                localStorage.setItem('applicationList', JSON.stringify(res.data))
                resolve(true);
            }) .catch((error) => {
                reject(error);
            });
        });
    }

    public getCDNRegionList() {
        return new Promise<CDNRegion[]>((resolve, reject) => {
            apiClient.get('/api/v1/admin/cdn-region/list')
                .then((res) => {
                    resolve(res.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }
    public updateCDNRegion(region: CDNRegion) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.put('/api/v1/admin/cdn-region/update', region).then((res) => {
                resolve(res.data.toString())
            }).catch((error) => {
                console.log("error"+error)
                reject(error)
            })
        })
    }
    public insertCDNRegion(region: CDNRegion) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.post('/api/v1/admin/cdn-region/insert', region).then((res) => {
                resolve(res.data.message)
            }).catch((error) => {
                reject(error)
            })
        })
    }
    public deactivateCDNRegion(cdnRegionDeactivate: CDNRegionDeactivate) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.put('/api/v1/admin/cdn-region/deactivate', cdnRegionDeactivate).then((res) => {
                resolve(res.data.message)
            }).catch((error) => {
                reject(error)
            })
        })
    }
    public getRegionList() {
        return new Promise<CDNRegion[]>((resolve, reject) => {
            apiClient.get('/api/v1/admin/region/list')
                .then((res) => {
                    resolve(res.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }
    public updateRegion(region: Region) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.put('/api/v1/admin/region/update', region).then((res) => {
                resolve(res.data.toString())
            }).catch((error) => {
                console.log("error"+error)
                reject(error)
            })
        })
    }
    public insertRegion(region: Region) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.post('/api/v1/admin/region/insert', region).then((res) => {
                resolve(res.data.message)
            }).catch((error) => {
                reject(error)
            })
        })
    }
    public getVideoQualityList() {
        return new Promise<VideoQuality[]>((resolve, reject) => {
            apiClient.get('/api/v1/admin/video-quality/list')
                .then((res) => {
                    resolve(res.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }
    public updateVideoQuality(videoQuality: VideoQuality) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.put('/api/v1/admin/video-quality/update', videoQuality).then((res) => {
                resolve(res.data.toString())
            }).catch((error) => {
                console.log("error"+error)
                reject(error)
            })
        })
    }
    public insertVideoQuality(videoQuality: VideoQuality) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.post('/api/v1/admin/video-quality/insert', videoQuality).then((res) => {
                resolve(res.data.message)
            }).catch((error) => {
                reject(error)
            })
        })
    }
    public getApplicationList() {
        return new Promise<Application[]>((resolve, reject) => {
            apiClient.get('/api/v1/admin/application/list').then((res) => {
                resolve(res.data); })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    public updateApplication(application: Application) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.put('/api/v1/admin/application/update', application).then((res) => {
                resolve(res.data.toString())
            }).catch((error) => {
                console.log("error"+error)
                reject(error)
            })
        })
    }
    public getSystemList() {
        return new Promise<System[]>((resolve, reject) => {
            apiClient.get('/api/v1/admin/system/list')
                .then((res) => {
                    resolve(res.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }
    public updateSystem(system: System) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.put('/api/v1/admin/system/update', system).then((res) => {
                resolve(res.data.toString())
            }).catch((error) => {
                console.log("error"+error)
                reject(error)
            })
        })
    }
    public insertSystem(system: System) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.post('/api/v1/admin/system/insert', system).then((res) => {
                resolve(res.data.message)
            }).catch((error) => {
                reject(error)
            })
        })
    }

    public getEmailListForCampaigns(emailRequest: EmailRequest) {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.post('/api/v1/admin/email/list', emailRequest)
                .then((res) => {
                    try {
                        FunctionService.downloadEmailsAsCSV(res.data, emailRequest);
                        resolve(true);
                    } catch (error) {
                        reject(error);
                    }
                }).catch((error) => {
                    reject(error);
                });
        });
    }

    public getUserStaticData() {
        return new Promise<boolean>((resolve, reject) => {
            apiClient.get('/api/v1/user/static').then((res) => {
                if (res.status === 200 && res.data !== null) {
                    const newRegions:Record<string, string> = { "null": "Automatic" };
                    for (const [key, value] of Object.entries(res.data.regions)) {
                        if (key !== "null") {
                            if (typeof value === "string") {
                                newRegions[key] = value;
                            }
                        }
                    }
                    sessionStorage.setItem('regions', JSON.stringify(newRegions))
                    sessionStorage.setItem('videoQualityList', JSON.stringify(res.data.videoQualities))
                    resolve(true)
                } else {
                    resolve(false)
                }
            }).catch((error) => {
                reject(error)
            })
        })
    }

    // Bootstrap payload for the calendar admin screens (CalLeagues/CalLeagueEdit, CalMatches
    // and its two slot editors) - enum option lists plus the editable lead/trail/pad-setup
    // defaults, cached in sessionStorage under one key since it's all one cohesive feature
    // area consumed together, unlike getUserStaticData's regions/videoQualityList above
    // (which get split because unrelated screens use them independently). Unlike that
    // pattern, this resolves with the parsed data directly rather than requiring the caller
    // to also read sessionStorage itself.
    public getCalendarStaticData(): Promise<CalendarStaticData> {
        return new Promise((resolve, reject) => {
            const stored = sessionStorage.getItem('calendarStatic');
            if (stored) {
                resolve(JSON.parse(stored));
                return;
            }
            apiClient.get('/api/v1/admin/cal-league/static').then((res) => {
                if (res.status === 200 && res.data !== null) {
                    sessionStorage.setItem('calendarStatic', JSON.stringify(res.data));
                    resolve(res.data);
                } else {
                    reject(new Error('Failed to load calendar static data'));
                }
            }).catch((error) => {
                reject(error);
            });
        });
    }
}

export default new AdminService ()